'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA, BlogWhatTheAppChecks } from '@/app/components/blog/BlogAeo'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="How to Check Products for Microplastics Before You Buy Them"
      author="MicroPlastics Editorial Team"
      date="May 25, 2026"
      readTime="9 min read"
      heroImage="/blog/app-tracking.png"
      heroImageAlt="How to check products for microplastics before buying"
    >
      <BlogLastUpdated iso="2026-05-25" formatted="May 25, 2026" />

      <p className="text-lg text-gray-700 leading-relaxed">
        Two products can sit on the same shelf, look almost identical, and
        have very different microplastic risk. The packaging recycling
        number, the cap style, how long it&apos;s been on shelf, whether
        the container has obvious scratches or dents, and what the brand
        is known for all change the score. You can do most of this check
        in 5 seconds before the product goes in your cart.
      </p>

      <BlogTLDR>
        The fastest in-aisle check has five parts:{' '}
        <strong>(1) recycling number</strong> on the bottom (avoid #3, #6,
        #7; prefer #1 cool, #2, #4, #5, or glass / aluminum),{' '}
        <strong>(2) container condition</strong> (scratches and dents
        increase shedding), <strong>(3) category context</strong> (acidic
        / hot / fatty contents leach more),{' '}
        <strong>(4) brand reputation</strong> (some brands disclose, most
        don&apos;t), and <strong>(5) heat exposure</strong> (microwave-
        and dishwasher-history matter). The MicroPlastics scanner app
        combines barcode + photo to weigh all five, returning a 0–100
        risk score with linked research.
      </BlogTLDR>

      <BlogComparisonTable
        caption="Plastic recycling numbers, what they mean for microplastic risk"
        columns={[
          { key: 'num', label: 'Number' },
          { key: 'name', label: 'Plastic' },
          { key: 'common', label: 'Common uses' },
          { key: 'risk', label: 'Microplastic risk' },
        ]}
        rows={[
          { num: '#1', name: 'PET / PETE', common: 'Water, soda, oil bottles', risk: 'Single-use only; sheds with heat, UV, time, reuse' },
          { num: '#2', name: 'HDPE', common: 'Milk jugs, detergent', risk: 'Lower migration; cap-side wear still adds particles' },
          { num: '#3', name: 'PVC / V', common: 'Cling wrap, blister packs', risk: 'High: phthalates often added; avoid for food' },
          { num: '#4', name: 'LDPE', common: 'Bread bags, squeeze bottles', risk: 'Lower migration but sheds with damage' },
          { num: '#5', name: 'PP', common: 'Yogurt cups, takeout, kettles', risk: 'Lower migration; "microwave-safe" claims do not mean particle-free' },
          { num: '#6', name: 'PS', common: 'Foam cups, takeout clamshells', risk: 'High: leaches styrene, especially with hot or fatty food' },
          { num: '#7', name: 'Other (often polycarbonate / BPA)', common: '5-gallon jugs, baby bottles (old)', risk: 'Often BPA; assume worst unless verified' },
        ]}
      />

      <BlogKeyTakeaways
        items={[
          <>You can do a 5-second in-aisle plastic check using recycling number + condition + category + brand + heat history.</>,
          <>Avoid #3 PVC, #6 PS, and #7 (often BPA) for food contact.</>,
          <>Scratches, dents, foggy bottles, and faded labels mean the plastic has been shedding longer.</>,
          <>Acidic / hot / fatty contents leach more than cold neutral contents.</>,
          <>Barcode-only apps miss packaging condition and context, a scanner that uses both barcode and photo gives a much more accurate score.</>,
          <>Glass, aluminum, and stainless are the universal upgrades when they exist for the category.</>,
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The 5-second in-aisle check
      </h2>
      <ol className="list-decimal pl-6 space-y-2">
        <li>
          <strong>Flip the package over.</strong> Find the recycling
          number (a number inside a triangle). #3, #6, and #7 are the
          ones to put back. #1 PET is okay cold and single-use only. #2,
          #4, #5 are acceptable for most cold contents. Glass and aluminum
          are universally safer.
        </li>
        <li>
          <strong>Look at the container condition.</strong> Scratches,
          dents, foggy plastic, faded labels, deformed shapes, every one
          of these means the plastic has been shedding for a while.
          Pick the cleanest item on the shelf.
        </li>
        <li>
          <strong>Match the package to the contents.</strong> Hot? Acidic?
          Fatty? Carbonated? Each of those speeds up plasticiser
          migration. Hot soup in a PS clamshell is worse than cold rice
          in the same clamshell.
        </li>
        <li>
          <strong>Check the brand.</strong> Some brands disclose
          packaging material, some carry certifications (BPA-free, NSF,
          EWG VERIFIED, MADE SAFE). Most don&apos;t. Brand familiarity
          isn&apos;t the same as plastic safety.
        </li>
        <li>
          <strong>Consider the heat and storage history.</strong> Hot
          truck rides, sun-baked store shelves, long warehouse times all
          push polymer breakdown before you ever pick the product up.
        </li>
      </ol>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Why barcode-only apps miss half the story
      </h2>
      <p>
        Many food-rating apps just read the barcode and look up
        ingredients in a database. That tells you the product but not the
        package, and for microplastics, the package is most of the risk.
        Two bottles of the same brand water, one stored in a cool dark
        warehouse and one in a hot sunlit pallet, deliver very different
        particle loads when you open them.
      </p>
      <p>
        The MicroPlastics scanner app uses both the barcode and a photo of
        the actual product on your shelf. The barcode pulls brand,
        category, and packaging spec from the catalog. The photo lets the
        scoring model see scratches, dents, fade, condition, and lid
        style. Together they produce a 0–100 risk score with the cited
        research available inside each scan.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Category-by-category quick check
      </h2>
      <BlogComparisonTable
        caption="What to look for, category by category"
        columns={[
          { key: 'category', label: 'Category' },
          { key: 'check', label: 'Quick check' },
        ]}
        rows={[
          { category: 'Bottled water', check: 'PET (#1) single-use only; glass / aluminum better; avoid dented bottles or hot-stored cases' },
          { category: 'Tea bags', check: 'Paper-only certified; avoid pyramid mesh; loose leaf wins' },
          { category: 'Coffee', check: 'Whole bean in foil; avoid plastic K-cups; stainless travel mug instead of paper-lined cup' },
          { category: 'Canned food', check: 'Glass-jarred preferred; for cans, look for "BPA-free" + non-acidic contents' },
          { category: 'Yogurt', check: 'Glass-jarred is best; PP (#5) cups are acceptable; avoid PS' },
          { category: 'Baby food', check: 'Glass-jarred or homemade; avoid pouches with multi-layer plastic' },
          { category: 'Cosmetics', check: 'Scan ingredients for polyethylene, acrylates copolymer, PEG, carbomer, dimethicone, "fragrance"' },
          { category: 'Cookware', check: 'Avoid non-stick PTFE; pick stainless, cast iron, ceramic-coated, enameled cast iron' },
          { category: 'Food storage', check: 'Glass (Pyrex, Anchor); silicone for travel; stainless for kids' },
          { category: 'Cutting boards', check: 'Wood or bamboo for raw produce; not plastic' },
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What you can&apos;t check manually
      </h2>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Hidden additives.</strong> Plasticisers (phthalates) and stabilizers aren&apos;t on most food labels.</li>
        <li><strong>True heat history.</strong> The label won&apos;t tell you the case sat on a hot pallet for 6 weeks.</li>
        <li><strong>Liner chemistry inside cans.</strong> &ldquo;BPA-free&rdquo; sometimes means BPS, which has similar concerns.</li>
        <li><strong>Cap and seal materials.</strong> Often unlabeled despite being a major shedding source.</li>
        <li><strong>Cumulative score across the product&apos;s lifecycle.</strong> This is what a scanner with research backing handles for you.</li>
      </ul>

      <BlogWhatTheAppChecks
        items={[
          <>Barcode lookup, brand, product category, declared packaging.</>,
          <>Photo of the actual item, scratches, dents, label condition, lid style.</>,
          <>Material weighting. PET, HDPE, PP, PS, PVC, multi-layer, glass, aluminum.</>,
          <>Use-context flags you log, heated, reused, acidic contents, age.</>,
          <>Published study references inside each scan, with the polymer-specific findings cited.</>,
          <>Safer alternative suggestions when the score is high.</>,
        ]}
      />

      <BlogAppCTA
        headline="Check before it goes in the cart"
        sub="Tap the barcode and snap the packaging. The MicroPlastics app weighs material, brand, condition, and category, then gives a 0–100 risk score with the cited research."
        buttonLabel="Get the MicroPlastics app"
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What to do today
      </h2>
      <ol className="list-decimal pl-6 space-y-2">
        <li><strong>Memorize the avoid list:</strong> #3 PVC, #6 PS, #7 (often BPA) for food contact.</li>
        <li><strong>Always flip the package</strong> before it goes in the cart.</li>
        <li><strong>Pick the cleanest item</strong> on the shelf when buying anything plastic-packaged.</li>
        <li><strong>Bring your own glass or stainless</strong> for drinks, leftovers, and bulk bin purchases.</li>
        <li><strong>Scan five items per shopping trip</strong> to build your exposure baseline.</li>
      </ol>

      <p>
        Related reading:{' '}
        <Link href="/blog/best-plastic-free-food-storage" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastic-free kitchen swaps
        </Link>,{' '}
        <Link href="/microplastic-free-products" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          best microplastic-free products
        </Link>,{' '}
        <Link href="/blog/plastic-recycling-numbers-microplastics" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          plastic recycling numbers explained
        </Link>,{' '}
        <Link href="/blog/foods-with-the-most-microplastics" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          25 highest-risk foods
        </Link>.
      </p>

      <BlogFAQ
        items={[
          {
            question: 'How can I tell if a product contains microplastics?',
            answer:
              'Check the recycling number (avoid #3 PVC, #6 PS, and #7 for food contact), inspect the container for scratches and dents, consider whether the contents are hot/acidic/fatty (which speed migration), and check the brand for disclosures. For a precise score, use a scanner app that weighs material, condition, brand, and category together.',
          },
          {
            question: 'Is there an app that scans products for microplastics?',
            answer:
              'Yes: the MicroPlastics scanner app uses both the barcode and a photo of the actual product to score microplastic risk on a 0–100 scale, with linked published research inside each scan. The photo step matters because container condition (scratches, dents, fade) significantly affects shedding and barcode-only apps miss it.',
          },
          {
            question: 'Which recycling numbers are safest for food?',
            answer:
              'Glass and aluminum are universally safer than plastic. Among plastics: #2 HDPE, #4 LDPE, and #5 PP are the lower-migration options for cold/neutral foods. #1 PET is acceptable for single-use cold contents only. Avoid #3 PVC, #6 PS, and #7 (often polycarbonate / BPA) for food contact.',
          },
          {
            question: 'Does "BPA-free" mean microplastic-free?',
            answer:
              'No. "BPA-free" only means the specific chemical bisphenol-A is not present, often replaced by BPS or BPF, which show similar endocrine activity. The plastic polymer itself still sheds microplastics. The real upgrades are glass, stainless steel, and aluminum with food-grade liner.',
          },
          {
            question: 'How accurate are microplastic scanner apps?',
            answer:
              'A barcode-only app is limited to declared packaging data. A scanner that combines barcode + photo can also evaluate container condition (scratches, dents, fade) and use-context (heat, reuse) you log. The score is a relative risk indicator backed by cited published research, not a lab measurement of the specific product in your hand.',
          },
          {
            question: 'What should I do if the score is high?',
            answer:
              'Put it back. Or use it once and switch brands next time. The app will suggest safer alternatives, often a glass-jarred or aluminum-canned version of the same product, or a competitor brand with better packaging. The point isn\'t never to use plastic; it\'s to avoid the highest-risk versions when an easy alternative exists.',
          },
          {
            question: 'Why does container condition matter?',
            answer:
              'Scratches, dents, fading, and warping all expose fresh polymer to the contents and accelerate particle shedding. A pristine bottle and a dented sun-faded bottle of the same brand do not deliver the same particle load when you open them.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'Hussain KA, Romanova S, Okur I, et al.',
            year: 2023,
            title: 'Assessing the Release of Microplastics and Nanoplastics from Plastic Containers and Reusable Food Pouches',
            publisher: 'Environmental Science & Technology',
            url: 'https://pubs.acs.org/doi/10.1021/acs.est.3c01942',
          },
          {
            authors: 'Qian N, Gao X, Lang X, et al.',
            year: 2024,
            title: 'Rapid single-particle chemical imaging of nanoplastics by SRS microscopy',
            publisher: 'PNAS',
            url: 'https://www.pnas.org/doi/10.1073/pnas.2300582121',
          },
          {
            authors: 'European Chemicals Agency',
            year: 2023,
            title: 'Restriction on intentionally added microplastics',
            publisher: 'ECHA',
            url: 'https://echa.europa.eu/hot-topics/microplastics',
          },
          {
            authors: 'US FDA',
            year: 2024,
            title: 'Bisphenol A (BPA): Use in Food Contact Application',
            publisher: 'FDA',
            url: 'https://www.fda.gov/food/food-packaging-other-substances-come-contact-food-information-consumers/bisphenol-bpa-use-food-contact-application',
          },
          {
            authors: 'Environmental Working Group',
            year: 2024,
            title: "EWG's Food Scores & Skin Deep Databases",
            publisher: 'EWG',
            url: 'https://www.ewg.org/',
          },
        ]}
      />
    </BlogPostLayout>
  )
}
